import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Plus, Pencil, Trash2, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import CustomerFormDialog from '@/components/CustomerFormDialog';

export default function Customers() {
  const { API } = useContext(AuthContext);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      loadCustomers();
    }, 300); // Debounce search

    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      console.log('Loading customers...', { page, search });
      const response = await axios.get(`${API}/customers`, {
        params: {
          page,
          limit: 10,
          search
        }
      });
      console.log('Customers response:', response.data);
      setCustomers(response.data.items);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error('Error loading customers:', error);
      toast.error(`Error al cargar clientes: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setDialogOpen(true);
  };

  const handleDelete = async (customer) => {
    if (!window.confirm(`¿Estás seguro de que deseas eliminar a ${customer.name}?`)) {
      return;
    }
    try {
      await axios.delete(`${API}/customers/${customer.id}`);
      toast.success('Cliente eliminado exitosamente');
      loadCustomers();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(error.response?.data?.detail || 'Error al eliminar cliente');
    }
  };

  const handleCreateOpen = () => {
    setEditingCustomer(null);
    setDialogOpen(true);
  };

  const handleDialogSuccess = () => {
    loadCustomers();
  };

  if (loading) {
    return <div className="p-8">Cargando...</div>;
  }

  return (
    <div className="p-8 space-y-6" data-testid="customers-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold">Clientes</h1>
          <p className="text-muted-foreground">Gestiona tu cartera de clientes</p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre, RUT..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1); // Reset to page 1 on search
              }}
              className="pl-8"
            />
          </div>
          <Button data-testid="create-customer-button" onClick={handleCreateOpen}>
            <Plus className="mr-2 h-4 w-4" />
            Nuevo Cliente
          </Button>
        </div>
      </div>

      {/* Customer Form Dialog */}
      <CustomerFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSuccess={handleDialogSuccess}
        editingCustomer={editingCustomer}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {customers.map((customer) => (
          <Card key={customer.id || customer._id} className="hover:shadow-md transition-all">
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <CardTitle className="text-lg font-bold">{customer.name}</CardTitle>
                <div className="flex space-x-1">
                  <Button variant="ghost" size="icon" onClick={() => handleEdit(customer)} className="h-8 w-8 text-gray-400 hover:text-gray-600" title="Editar">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(customer)} className="h-8 w-8 text-gray-400 hover:text-red-500" title="Eliminar">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <CardDescription>RUT: {customer.rut}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {customer.email && <p><strong>Email:</strong> {customer.email}</p>}
              {customer.phone && <p><strong>Teléfono:</strong> {customer.phone}</p>}
              {customer.address && <p><strong>Dirección:</strong> {customer.address}, {customer.commune}</p>}
              <div className="pt-2 border-t">
                <p className="font-mono"><strong>Saldo:</strong> ${typeof customer.balance === 'number' ? customer.balance.toFixed(2) : '0.00'}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {customers.length === 0 && !loading && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-muted-foreground">No se encontraron clientes</p>
            {search && <p className="text-sm text-muted-foreground">Intenta con otra búsqueda</p>}
          </CardContent>
        </Card>
      )}

      {/* Pagination Controls */}
      <div className="flex justify-between items-center mt-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1}
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Anterior
        </Button>
        <span className="text-sm text-muted-foreground">
          Página {page} de {totalPages || 1}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={page >= totalPages}
        >
          Siguiente
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}